import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Eye,
  Film,
  Gamepad2,
  Instagram,
  Loader2,
  Sparkles,
  Twitch,
  Twitter,
  Video,
  Youtube,
  Zap,
} from "lucide-react";
import DOMPurify from "dompurify";
import sanitizeFilename from "sanitize-filename";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../Context/firebaseConfig";
import SEO from "../Components/SEO";
import { normalizeSocialUrl } from "../utils/socialLinks";
import { getSafeNextPath, withNextPath } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const DEFAULT_AVATAR =
  "https://static.vecteezy.com/system/resources/previews/036/280/650/original/default-avatar-profile-icon-social-media-user-image-gray-avatar-icon-blank-profile-silhouette-illustration-vector.jpg";

const SOCIAL_OPTIONS = [
  {
    key: "youtube",
    label: "YouTube",
    Icon: Youtube,
    placeholder: "youtube.com/@username",
  },
  {
    key: "tiktok",
    label: "TikTok",
    Icon: Video,
    placeholder: "tiktok.com/@username",
  },
  {
    key: "twitch",
    label: "Twitch",
    Icon: Twitch,
    placeholder: "twitch.tv/username",
  },
  {
    key: "instagram",
    label: "Instagram",
    Icon: Instagram,
    placeholder: "instagram.com/username",
  },
  {
    key: "twitter",
    label: "X / Twitter",
    Icon: Twitter,
    placeholder: "x.com/username",
  },
];

const CREATOR_INTENTS = [
  { key: "browsing", label: "Just browsing", desc: "Looking around for now" },
  { key: "soon", label: "Need edits soon", desc: "Within the next few weeks" },
  { key: "ready", label: "Ready to order", desc: "I have a project lined up" },
];

const SOCIAL_KEYS = SOCIAL_OPTIONS.map((option) => option.key);

function sanitizeValue(value) {
  if (typeof value !== "string") return value;
  return DOMPurify.sanitize(value);
}

function sanitizeProfilePatch(patch) {
  return Object.entries(patch).reduce((acc, [key, value]) => {
    if (key === "profilePicUrl") {
      acc[key] = value;
      return acc;
    }

    if (key === "socialPlatform") {
      acc[key] = SOCIAL_KEYS.includes(value) ? value : "";
      return acc;
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      acc[key] = sanitizeProfilePatch(value);
      return acc;
    }

    acc[key] = sanitizeValue(value);
    return acc;
  }, {});
}

function formatContentFocus(value = "") {
  const labels = {
    "custom-editing": "Custom editing",
    "clipping-campaigns": "Clipping campaigns",
    "editing-and-clipping": "Editing and clipping",
    "short-form-edits": "Short form edits",
    "twitch-vod-edits": "Twitch VOD edits",
    "youtube-gaming-videos": "YouTube gaming videos",
    "thumbnails-other": "Thumbnails or other",
  };

  return labels[value] || "";
}

function GlowBg() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute -left-24 -top-32 h-[420px] w-[420px] rounded-full opacity-25 blur-3xl"
        style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
      />
      <div
        className="absolute -bottom-32 -right-24 h-[480px] w-[480px] rounded-full opacity-25 blur-3xl"
        style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
      />
    </div>
  );
}

function GlassCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border border-white/10 bg-white/[0.03] p-5 shadow-[0_20px_60px_-35px_rgba(168,85,247,0.7)] backdrop-blur-xl sm:p-7 ${className}`}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      style={{
        background: `linear-gradient(135deg, ${MINT}, #34d399)`,
        boxShadow: `0 16px 50px -14px ${MINT}`,
      }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-semibold text-white/75 transition hover:bg-white/[0.07] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(sanitizeValue(e.target.value))}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20"
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(sanitizeValue(e.target.value))}
      placeholder={placeholder}
      rows={rows}
      className="w-full resize-none rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-white outline-none transition placeholder:text-white/35 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20"
    />
  );
}

function AvatarPicker({ preview, onFile }) {
  return (
    <label className="flex cursor-pointer items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.03] p-4 transition hover:bg-white/[0.05]">
      <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]">
        {preview && preview !== DEFAULT_AVATAR ? (
          <img src={preview} alt="" className="h-full w-full object-cover" />
        ) : (
          <Camera size={22} className="text-white/45" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white">Add a profile photo</p>
        <p className="mt-1 text-xs leading-relaxed text-white/50">
          A clear photo makes profiles feel more trustworthy. JPG, PNG, or WebP.
        </p>
      </div>
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
    </label>
  );
}

function SocialPicker({ platform, handle, onPlatform, onHandle }) {
  const activeOption =
    SOCIAL_OPTIONS.find((option) => option.key === platform) || SOCIAL_OPTIONS[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {SOCIAL_OPTIONS.map(({ key, label, Icon }) => {
          const active = platform === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onPlatform(key)}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition"
              style={{
                borderColor: active ? `${PURPLE}80` : "rgba(255,255,255,0.1)",
                background: active ? `${PURPLE}22` : "rgba(255,255,255,0.03)",
                color: active ? "white" : "rgba(255,255,255,0.68)",
              }}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>
      <TextInput
        value={handle}
        onChange={onHandle}
        placeholder={activeOption.placeholder}
        maxLength={180}
      />
      <p className="text-xs text-white/40">
        Add your {activeOption.label} link or handle. We will save it as a
        clickable profile link.
      </p>
    </div>
  );
}

function ProgressHeader({ step, total, role, mainGame, contentFocus }) {
  const percent = Math.round((step / total) * 100);
  const contentFocusLabel = formatContentFocus(contentFocus);
  return (
    <div className="mb-5">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/45">
            <Sparkles size={13} style={{ color: MINT }} />
            Profile setup
          </div>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {role === "Creator"
              ? "Help editors understand your content"
              : contentFocus === "clipping-campaigns"
                ? "Help streamers trust your clipping style"
                : "Help creators trust your editing style"}
          </h1>
          <p className="mt-1.5 text-sm text-white/55">
            Step {step} of {total}. You can finish the rest later.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {role && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/65">
              <Film size={12} style={{ color: role === "Creator" ? PURPLE : MINT }} />
              {role}
            </span>
          )}
          {mainGame && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-200">
              <Gamepad2 size={12} />
              {mainGame}
            </span>
          )}
          {contentFocusLabel && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-400/25 bg-purple-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-purple-100">
              <Zap size={12} />
              {contentFocusLabel}
            </span>
          )}
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${percent}%`,
            background: `linear-gradient(90deg, ${MINT}, ${PURPLE})`,
            boxShadow: `0 0 18px ${MINT}80`,
          }}
        />
      </div>
    </div>
  );
}

function PreviewCard({ data, role, username, mainGame }) {
  const selectedSocialValue = data.socialLinks?.[data.socialPlatform] || "";
  const contentFocusLabel = formatContentFocus(data.contentFocus);
  const completed = [
    !!data.profilePicUrl && data.profilePicUrl !== DEFAULT_AVATAR,
    !!data.tagline,
    !!data.about,
    !!selectedSocialValue,
    role === "Creator" ? !!data.questionOne : !!data.videoOne,
  ].filter(Boolean).length;
  const strength = Math.round((completed / 5) * 100);

  return (
    <GlassCard className="sticky top-6">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/45">
        <Eye size={13} />
        Live preview
      </div>
      <div className="mt-5 flex items-center gap-3">
        <div
          className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]"
        >
          {data.profilePicUrl && data.profilePicUrl !== DEFAULT_AVATAR ? (
            <img src={data.profilePicUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Sparkles size={20} className="text-white/45" />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">
            @{username || "yourname"}
          </p>
          <p className="text-xs text-white/45">{role || "Kre8 member"}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {mainGame && (
          <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-100">
            {mainGame}
          </span>
        )}
        {selectedSocialValue && (
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium text-white/65">
            {selectedSocialValue}
          </span>
        )}
        {contentFocusLabel && (
          <span className="rounded-full border border-purple-400/25 bg-purple-500/10 px-3 py-1 text-[11px] font-medium text-purple-100">
            {contentFocusLabel}
          </span>
        )}
      </div>
      {data.tagline && <p className="mt-4 text-sm font-semibold text-white">{data.tagline}</p>}
      {data.about && <p className="mt-2 text-sm leading-relaxed text-white/60">{data.about}</p>}
      <div className="mt-5">
        <div className="mb-2 flex justify-between text-xs">
          <span className="text-white/45">Profile strength</span>
          <span className="font-semibold" style={{ color: strength >= 60 ? MINT : "white" }}>
            {strength}%
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${strength}%`,
              background: `linear-gradient(90deg, ${MINT}, ${PURPLE})`,
            }}
          />
        </div>
      </div>
    </GlassCard>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = getSafeNextPath(searchParams.get("next"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [step, setStep] = useState(1);
  const onboardingStartedRef = useRef(false);
  const [profile, setProfile] = useState({
    username: "",
    role: "",
    mainGame: "",
    contentFocus: "",
    profilePicUrl: "",
    tagline: "",
    about: "",
    socialPlatform: "",
    socialLinks: {
      youtube: "",
      tiktok: "",
      twitch: "",
      instagram: "",
      twitter: "",
    },
    videoOne: "",
    questionOne: "",
    questionTwo: "",
    questionFour: "",
    creatorIntent: "",
  });

  const role = profile.role;
  const totalSteps = 3;
  const isCreator = role?.toLowerCase() === "creator";
  const profileRoute = isCreator ? "/creatorPortfolio" : "/editorPortfolio";
  const completionRoute = nextPath === "/" ? profileRoute : nextPath;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate(withNextPath("/signup", nextPath));
        return;
      }

      try {
        setUser(currentUser);
        const snap = await getDoc(doc(db, "Users", currentUser.uid));
        const data = snap.exists() ? snap.data() : {};
        const profileInfo = data.profileInfo || {};
        const socials = data.socials || {};
        const videoLinks = data.videoLinks || {};
        const questions = profileInfo.questions || {};
        const firstSocial =
          SOCIAL_OPTIONS.find((item) => socials[item.key])?.key || "youtube";
        const loadedRole = profileInfo.role || "Creator";

        if (profileInfo.onboardingCompleted) {
          navigate(
            nextPath === "/"
              ? loadedRole?.toLowerCase() === "creator"
                ? "/creatorPortfolio"
                : "/editorPortfolio"
              : nextPath,
            { replace: true }
          );
          return;
        }

        setProfile({
          username: profileInfo.username || "",
          role: loadedRole,
          mainGame:
            typeof profileInfo.mainGame === "string"
              ? profileInfo.mainGame
              : profileInfo.mainGame?.categoryName || profileInfo.mainGame?.name || "",
          contentFocus: profileInfo.contentFocus || "",
          profilePicUrl: profileInfo.profilePicUrl || "",
          tagline: profileInfo.tagline || "",
          about: profileInfo.about || "",
          socialPlatform: firstSocial,
          socialLinks: {
            youtube: socials.youtube || "",
            tiktok: socials.tiktok || "",
            twitch: socials.twitch || "",
            instagram: socials.instagram || "",
            twitter: socials.twitter || "",
          },
          videoOne: videoLinks.videoOne || "",
          questionOne: questions.questionOne || "",
          questionTwo: questions.questionTwo || "",
          questionFour: questions.questionFour || "",
          creatorIntent: profileInfo.creatorIntent || "",
        });
        if (!onboardingStartedRef.current) {
          onboardingStartedRef.current = true;
          trackEvent("onboarding_started");
        }
      } catch (err) {
        console.error(err);
        setError("Could not load onboarding. Refresh and try again.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, nextPath]);

  const update = (patch) => {
    setError("");
    setProfile((prev) => ({ ...prev, ...sanitizeProfilePatch(patch) }));
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    setSelectedFile(file);
    update({ profilePicUrl: URL.createObjectURL(file) });
  };

  const uploadProfilePhoto = async () => {
    if (!selectedFile || !user) return profile.profilePicUrl;
    const cleanName = sanitizeFilename(selectedFile.name) || "profile-photo";
    const safeFile = new File([selectedFile], cleanName, { type: selectedFile.type });
    const storageRef = ref(storage, `profilePics/${user.uid}`);
    const uploadResult = await uploadBytes(storageRef, safeFile);
    return getDownloadURL(uploadResult.ref);
  };

  const buildPayload = async (completed) => {
    const imageUrl = await uploadProfilePhoto();
    const selectedSocialPlatform = SOCIAL_KEYS.includes(profile.socialPlatform)
      ? profile.socialPlatform
      : "youtube";
    const cleanSocial = DOMPurify.sanitize(
      normalizeSocialUrl(
        selectedSocialPlatform,
        profile.socialLinks?.[selectedSocialPlatform] || ""
      )
    );
    const payload = {
      profileInfo: {
        profilePicUrl: imageUrl || DEFAULT_AVATAR,
        tagline: DOMPurify.sanitize(profile.tagline.trim()),
        about: DOMPurify.sanitize(profile.about.trim()),
        questions: {
          questionOne: DOMPurify.sanitize(profile.questionOne.trim()),
          questionTwo: DOMPurify.sanitize(profile.questionTwo.trim()),
          questionFour: DOMPurify.sanitize(profile.questionFour.trim()),
        },
        creatorIntent: DOMPurify.sanitize(profile.creatorIntent),
        onboardingCompleted: completed,
        onboardingSkipped: !completed,
      },
      socials: {
        [selectedSocialPlatform]: cleanSocial,
      },
    };

    if (!isCreator) {
      payload.videoLinks = {
        videoOne: DOMPurify.sanitize(profile.videoOne.trim()),
      };
    }

    return payload;
  };

  const persist = async (completed = false) => {
    if (!user) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "Users", user.uid), await buildPayload(completed), {
        merge: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    try {
      await persist(false);
      if (step < totalSteps) {
        trackEvent("onboarding_step_completed", { step_number: step });
        setStep((value) => value + 1);
      } else {
        await persist(true);
        trackEvent("onboarding_completed");
        navigate(completionRoute);
      }
    } catch (err) {
      console.error(err);
      setError("Could not save onboarding. Try again.");
    }
  };

  const finishLater = async () => {
    try {
      await persist(false);
      trackEvent("onboarding_skipped", { step_number: step });
      navigate(completionRoute);
    } catch (err) {
      console.error(err);
      setError("Could not save onboarding. Try again.");
    }
  };

  const stepContent = useMemo(() => {
    if (step === 1) {
      return (
        <GlassCard>
          <h2 className="text-xl font-bold text-white">Make the profile feel human</h2>
          <p className="mt-1 text-sm text-white/55">
            Signup already handled your role and main category. This is just trust-building.
          </p>
          <div className="mt-6 space-y-4">
            <AvatarPicker preview={profile.profilePicUrl} onFile={handleFile} />
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
                Tagline
              </label>
              <TextInput
                value={profile.tagline}
                onChange={(value) => update({ tagline: value })}
                placeholder={
                  isCreator
                    ? "Creator looking for sharp short form edits"
                    : profile.contentFocus === "clipping-campaigns"
                      ? "Clipper for Twitch streamers and short form campaigns"
                      : "Fast paced gaming editor for streamers and creators"
                }
                maxLength={120}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
                About
              </label>
              <Textarea
                value={profile.about}
                onChange={(value) => update({ about: value })}
                placeholder={
                  isCreator
                    ? "Tell editors what you make, your audience, and your content style."
                    : profile.contentFocus === "clipping-campaigns"
                      ? "Tell streamers what kind of clips you make, the games you understand, and how you spot moments."
                      : "Tell creators what you edit best, your turnaround style, and what makes your work strong."
                }
              />
            </div>
          </div>
        </GlassCard>
      );
    }

    if (step === 2) {
      return (
        <GlassCard>
          <h2 className="text-xl font-bold text-white">
            {isCreator
              ? "Share your main channel"
              : profile.contentFocus === "clipping-campaigns"
                ? "Show your clipping proof"
                : "Show your editing proof"}
          </h2>
          <p className="mt-1 text-sm text-white/55">
            {isCreator
              ? "Editors can understand your content faster when they can see your work."
              : profile.contentFocus === "clipping-campaigns"
                ? "Add one strong clip, Short, TikTok, Reel, or stream moment now. You can add more later."
                : "Add one strong edited video now. You can add the rest on your portfolio page."}
          </p>
          <div className="mt-6 space-y-5">
            <SocialPicker
              platform={profile.socialPlatform}
              handle={profile.socialLinks?.[profile.socialPlatform] || ""}
              onPlatform={(value) => update({ socialPlatform: value })}
              onHandle={(value) =>
                update({
                  socialLinks: {
                    ...profile.socialLinks,
                    [profile.socialPlatform]: value,
                  },
                })
              }
            />
            {!isCreator && (
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
                  {profile.contentFocus === "clipping-campaigns" ? "Clip link" : "Edited video link"}
                </label>
                <TextInput
                  value={profile.videoOne}
                  onChange={(value) => update({ videoOne: value })}
                  placeholder={
                    profile.contentFocus === "clipping-campaigns"
                      ? "https://www.tiktok.com/@..."
                      : "https://www.youtube.com/watch?v=..."
                  }
                  maxLength={220}
                />
              </div>
            )}
          </div>
        </GlassCard>
      );
    }

    return (
      <GlassCard>
        <h2 className="text-xl font-bold text-white">
          {isCreator ? "Help editors understand the work" : "Set expectations with creators"}
        </h2>
        <p className="mt-1 text-sm text-white/55">
          A little context now prevents messy projects later.
        </p>
        <div className="mt-6 space-y-5">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
              {isCreator
                ? "What do you need edited or clipped?"
                : profile.contentFocus === "clipping-campaigns"
                  ? "What makes a clipping campaign easy for you?"
                  : "What makes a project easy for you?"}
            </label>
            <Textarea
              value={profile.questionOne}
              onChange={(value) => update({ questionOne: value })}
              placeholder={
                isCreator
                  ? "Short form clips, captions, memes, pacing, hooks, and clean delivery."
                  : profile.contentFocus === "clipping-campaigns"
                    ? "Clear VOD links, timestamps, references, platform goals, and notes on what moments matter."
                    : "Clear raw files, references, deadlines, and notes on tone/music."
              }
              rows={5}
            />
          </div>

          {isCreator ? (
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
                How soon are you looking to order?
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                {CREATOR_INTENTS.map((item) => {
                  const active = profile.creatorIntent === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => update({ creatorIntent: item.key })}
                      className="rounded-2xl border p-4 text-left transition hover:bg-white/[0.05]"
                      style={{
                        borderColor: active ? `${MINT}80` : "rgba(255,255,255,0.1)",
                        background: active ? `${MINT}18` : "rgba(255,255,255,0.03)",
                      }}
                    >
                      <Zap size={16} style={{ color: active ? MINT : "rgba(255,255,255,0.6)" }} />
                      <p className="mt-2 text-sm font-semibold text-white">{item.label}</p>
                      <p className="mt-1 text-xs text-white/45">{item.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
                What do you bring to the table?
              </label>
              <Textarea
                value={profile.questionFour}
                onChange={(value) => update({ questionFour: value })}
                placeholder="Fast turnaround, strong hooks, subtitles, gaming pacing, revisions, and clear communication."
                rows={5}
              />
            </div>
          )}
        </div>
      </GlassCard>
    );
  }, [step, profile, isCreator]);

  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#07060d] text-white">
        <Loader2 size={24} className="animate-spin text-white/45" />
      </main>
    );
  }

  return (
    <main className="relative min-h-screen bg-[#07060d] text-white">
      <SEO
        title="Onboarding | Kre8"
        description="Private Kre8 profile onboarding."
        canonicalPath="/onboarding"
        noindex
      />
      <GlowBg />
      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-6 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
            <Sparkles size={12} style={{ color: MINT }} />
            Welcome to Kre8
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Let&apos;s finish your profile
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/58">
            You already picked your role and category during signup. These last details
            make your profile easier to trust and act on.
          </p>
        </div>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-100">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.45fr_1fr]">
          <div>
            <ProgressHeader
              step={step}
              total={totalSteps}
              role={role}
                mainGame={profile.mainGame}
                contentFocus={profile.contentFocus}
            />
            {stepContent}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              {step > 1 && (
                <GhostButton onClick={() => setStep((value) => Math.max(1, value - 1))} disabled={saving}>
                  <ArrowLeft size={16} />
                  Back
                </GhostButton>
              )}
              <button
                type="button"
                onClick={finishLater}
                disabled={saving}
                className="text-sm font-medium text-white/45 transition hover:text-white/75 disabled:opacity-50 sm:ml-1"
              >
                Finish later
              </button>
              <div className="sm:ml-auto">
                <PrimaryButton onClick={next} disabled={saving}>
                  {saving && <Loader2 size={15} className="animate-spin" />}
                  {step === totalSteps
                    ? isCreator
                      ? "Connect YouTube next"
                      : "Add more videos next"
                    : "Looks good"}
                  {!saving && <ArrowRight size={16} />}
                </PrimaryButton>
              </div>
            </div>
          </div>
          <PreviewCard
            data={profile}
            role={role}
            username={profile.username}
            mainGame={profile.mainGame}
          />
        </div>
      </div>
    </main>
  );
}
